/**
 * Runs a low-priority callback once the browser is idle (i.e. past first paint),
 * so non-critical third-party init (GTM, Intercom, …) stops competing with the
 * initial render on cold load. Falls back to a post-paint timeout where
 * `requestIdleCallback` is unavailable (Safari). Errors are swallowed so one
 * failing init can't break the others. See CM-3854.
 */
export const deferIdle = (fn: () => void, timeout = 3000): void => {
  const run = () => {
    try {
      fn()
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('deferIdle callback failed', err)
    }
  }

  const ric =
    typeof window !== 'undefined' ? window.requestIdleCallback : undefined

  if (typeof ric === 'function') {
    ric(run, { timeout })
  } else {
    // Safari has no requestIdleCallback. `timeout` is rIC's max-wait *deadline*,
    // not a delay — so we fire ASAP on the next macrotask rather than waiting
    // `timeout`ms. That still gets the work off the synchronous boot tick, which
    // is the goal; honouring `timeout` here would needlessly delay it.
    setTimeout(run, 1)
  }
}
